/**
 * Template Seeding Service
 *
 * API service for seeding templates from master_db (or an in-code industry
 * pack) to tenant databases. The backend exposes a single `POST
 * /platform-admin/templates/seed` endpoint that switches behavior on the
 * `source` field: `'master'` reads from master_db, `'pack:<key>'` reads from
 * the industry-pack registry. The optional `dryRun` flag runs the same
 * logic inside a transaction that always rolls back; the response then
 * carries `wouldCreate/wouldUpdate/wouldSkip` arrays in place of the live
 * counts so the operator can preview before committing.
 */

import apiClient from "./apiClient";
import { API_ENDPOINTS } from "../config/api";

/**
 * Template entity types that can be seeded
 */
export enum TemplateEntityType {
  PERMISSIONS = "permissions",
  ROLES = "roles",
  CONSENT_BASES = "consent_bases",
  STATUS_FLOWS = "status_flows",
  SOURCES = "sources",
  CHECKLIST_GROUPS = "checklist_groups",
  ACTIVITY_TYPES = "activity_types",
  TASK_TYPES = "task_types",
  FOLDER_TEMPLATES = "folder_templates",
  CATEGORIES = "categories",
  RESOURCE_LINKS = "resource_links",
  SMS_TEMPLATES = "sms_templates",
  EMAIL_TEMPLATES = "email_templates",
}

/**
 * Conflict strategy when seeding
 */
export enum SeedConflictStrategy {
  SKIP = "skip",
  OVERWRITE = "overwrite",
}

/**
 * Template metadata
 */
export interface TemplateEntityMetadata {
  value: TemplateEntityType;
  displayName: string;
  displayNameNo: string;
  description: string;
  descriptionNo: string;
  isCore: boolean;
}

/**
 * Available templates response
 */
export interface AvailableTemplatesResponse {
  templates: Record<
    TemplateEntityType,
    {
      count: number;
      displayName: string;
      displayNameNo: string;
      isCore: boolean;
    }
  >;
}

/**
 * Entity types metadata response
 */
export interface EntityTypesMetadataResponse {
  types: TemplateEntityMetadata[];
}

/**
 * Selectable types response
 */
export interface SelectableTypesResponse {
  types: TemplateEntityType[];
}

/**
 * Per-item summary mirrored from the backend `SeedItemSummary` DTO. The
 * `naturalKey` shape is entity-type-specific (e.g. `{ name }` for sources,
 * `{ name, assignmentType, appModule, targetEntity }` for checklist groups);
 * the frontend treats it as an opaque map for display purposes.
 */
export interface SeedItemSummary {
  naturalKey: Record<string, unknown>;
  reason?: string;
}

/**
 * Seed templates request
 *
 * `source` defaults server-side to `'master'`; existing callers that omit it
 * keep working unchanged. `dryRun` defaults server-side to `false`.
 */
export interface SeedTemplatesRequest {
  tenantId: string;
  environment?: string;
  entityTypes: TemplateEntityType[];
  conflictStrategy?: SeedConflictStrategy;
  source?: string;
  dryRun?: boolean;
}

/**
 * Single entity type result
 *
 * Live mode populates `created/updated/skipped` (counts) plus the matching
 * `*Items` arrays; dry-run mode populates `wouldCreate/wouldUpdate/wouldSkip`
 * arrays. The counts are still present in dry-run mode (zeroes) so existing
 * UI surfaces that read them stay safe.
 */
export interface SeedEntityTypeResult {
  entityType: TemplateEntityType;
  success: boolean;
  created: number;
  updated: number;
  skipped: number;
  error?: string;
  createdItems?: SeedItemSummary[];
  updatedItems?: SeedItemSummary[];
  skippedItems?: SeedItemSummary[];
  wouldCreate?: SeedItemSummary[];
  wouldUpdate?: SeedItemSummary[];
  wouldSkip?: SeedItemSummary[];
}

/**
 * Seed templates response
 */
export interface SeedTemplatesResponse {
  success: boolean;
  tenantId: string;
  environment: string;
  results: SeedEntityTypeResult[];
  error?: string;
  dryRun?: boolean;
}

/**
 * Result of syncing product system templates (from code) to a tenant.
 */
export interface SyncSystemTemplatesResponse {
  message: string;
  smsUpserted: number;
  smsDeactivated: number;
  emailUpserted: number;
  emailDeactivated: number;
}

/**
 * Result of syncing product system templates to ALL tenants.
 */
export interface SyncAllSystemTemplatesResponse {
  message: string;
  success: number;
  failed: number;
}

export const templateService = {
  /**
   * Get available templates from master database with counts
   */
  async getAvailableTemplates(): Promise<AvailableTemplatesResponse> {
    const response = await apiClient.get<AvailableTemplatesResponse>(
      API_ENDPOINTS.TEMPLATES
    );
    return response.data;
  },

  /**
   * Get metadata for all entity types
   */
  async getEntityTypesMetadata(): Promise<EntityTypesMetadataResponse> {
    const response = await apiClient.get<EntityTypesMetadataResponse>(
      API_ENDPOINTS.TEMPLATES_ENTITY_TYPES
    );
    return response.data;
  },

  /**
   * Get selectable (non-core) entity types
   */
  async getSelectableTypes(): Promise<SelectableTypesResponse> {
    const response = await apiClient.get<SelectableTypesResponse>(
      API_ENDPOINTS.TEMPLATES_SELECTABLE_TYPES
    );
    return response.data;
  },

  /**
   * Seed templates to a tenant
   */
  async seedTemplates(
    request: SeedTemplatesRequest
  ): Promise<SeedTemplatesResponse> {
    const response = await apiClient.post<SeedTemplatesResponse>(
      API_ENDPOINTS.TEMPLATES_SEED,
      request
    );
    return response.data;
  },

  /**
   * Sync product SYSTEM templates (from code) to a specific tenant + environment.
   * Upserts the current set and deactivates removed keys. Distinct from
   * `seedTemplates`, which copies CUSTOM templates from master_db and cannot
   * touch system templates.
   */
  async syncSystemTemplatesToTenant(request: {
    tenantId: string;
    environment: string;
  }): Promise<SyncSystemTemplatesResponse> {
    const response = await apiClient.post<SyncSystemTemplatesResponse>(
      API_ENDPOINTS.SYSTEM_TEMPLATES_SYNC_TENANT,
      request
    );
    return response.data;
  },

  /**
   * Sync product SYSTEM templates (from code) to ALL tenants (production env).
   */
  async syncSystemTemplatesToAll(): Promise<SyncAllSystemTemplatesResponse> {
    const response = await apiClient.post<SyncAllSystemTemplatesResponse>(
      API_ENDPOINTS.SYSTEM_TEMPLATES_SYNC_ALL,
      {}
    );
    return response.data;
  },
};
